import React, { useState, useEffect } from 'react';
import { Form, Button, Modal, Badge, Alert } from 'react-bootstrap';
import PicklistApi from '../../api/PicklistApi';
import { FaCheck, FaTimes } from 'react-icons/fa';

const PicklistValueSelector = ({
  field,
  value,
  onChange,
  disabled = false,
  isMultiSelect = false
}) => {
  const [picklistValues, setPicklistValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);

  const picklistApi = new PicklistApi();

  useEffect(() => {
    if (field && (field.data_type === 'picklist' || field.data_type === 'multipicklist')) {
      loadPicklistValues();
    }
  }, [field]);

  useEffect(() => {
    // Parse the current value to set selected values
    if (value) {
      if (isMultiSelect) {
        // For multipicklist, split by semicolon
        const values = value.split(';').filter(v => v.trim());
        setSelectedValues(values);
      } else {
        // For single picklist
        setSelectedValues([value]);
      }
    } else {
      setSelectedValues([]);
    }
  }, [value, isMultiSelect]);

  const loadPicklistValues = async () => {
    if (!field?.id) return;

    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.getActivePicklistValues(field.id);
      if (response.data.success) {
        setPicklistValues(response.data.data || []);
      } else {
        setError('Failed to load picklist values');
      }
    } catch (error) {
      console.error('Error loading picklist values:', error);
      setError('Failed to load picklist values: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleValueToggle = (valueKey) => {
    if (isMultiSelect) {
      setSelectedValues(prev => {
        if (prev.includes(valueKey)) {
          return prev.filter(v => v !== valueKey);
        } else {
          return [...prev, valueKey];
        }
      });
    } else {
      setSelectedValues([valueKey]);
    }
  };

  const handleApply = () => {
    if (isMultiSelect) {
      // Join with semicolon for multipicklist
      const newValue = selectedValues.join(';');
      onChange(newValue);
    } else {
      // Single value for picklist
      const newValue = selectedValues[0] || '';
      onChange(newValue);
    }
    setShowModal(false);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onChange('');
    setShowModal(false);
  };

  const getDisplayValue = () => {
    if (!value) return 'Select value...';

    if (isMultiSelect) {
      const values = value.split(';').filter(v => v.trim());
      if (values.length === 0) return 'Select values...';
      if (values.length === 1) return values[0];
      return `${values.length} values selected`;
    } else {
      return value;
    }
  };

  const getSelectedBadges = () => {
    if (!value) return null;

    const values = isMultiSelect ? value.split(';').filter(v => v.trim()) : [value];

    return (
      <div className="mt-2">
        {values.map((val, index) => (
          <Badge key={index} bg="primary" className="me-1 mb-1">
            {val}
          </Badge>
        ))}
      </div>
    );
  };

  if (!field || (field.data_type !== 'picklist' && field.data_type !== 'multipicklist')) {
    return null;
  }

  return (
    <>
      <Form.Control
        type="text"
        value={getDisplayValue()}
        readOnly
        disabled={disabled}
        onClick={() => !disabled && setShowModal(true)}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        placeholder={isMultiSelect ? 'Select values...' : 'Select value...'}
      />

      {getSelectedBadges()}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            Select {isMultiSelect ? 'Values' : 'Value'} for {field.field_label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-3">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="picklist-values-list">
              {picklistValues.length === 0 ? (
                <Alert variant="info">No picklist values available for this field.</Alert>
              ) : (
                picklistValues.map((picklistValue) => (
                  <div
                    key={picklistValue.id}
                    className={`picklist-value-item ${selectedValues.includes(picklistValue.value_key) ? 'selected' : ''}`}
                    onClick={() => handleValueToggle(picklistValue.value_key)}
                    style={{
                      padding: '8px 12px',
                      margin: '4px 0',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: selectedValues.includes(picklistValue.value_key) ? '#e3f2fd' : 'white',
                      borderColor: selectedValues.includes(picklistValue.value_key) ? '#2196f3' : '#dee2e6'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{picklistValue.value_label}</span>
                      {selectedValues.includes(picklistValue.value_key) && (
                        <FaCheck className="text-primary" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PicklistValueSelector;
